import {escapeString} from '@github-ui/filter-core'

export type TokenType = 'label' | 'project' | 'author' | 'assignee' | 'milestone' | 'review' | 'type'

const tokensByType = (type: TokenType) =>
  new RegExp(`\\b${type}:("([^"]*)"|[^,\\s\\)]+)(\\s*,\\s*("(?:\\"|.)*?"|[^,\\s]+))*`, 'gim')

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const getTokensByType = (searchQuery: string, type: TokenType) => {
  const matches = searchQuery.match(tokensByType(type)) ?? []
  return matches.flatMap(match =>
    match
      .slice(`${type}:`.length)
      .split(',')
      .map(token => token.trim()),
  )
}

function cleanConsecutiveSpaces(query: string): string {
  let inQuotes = false
  let newQuery = ''
  let previousCharacter = ''

  for (const character of query) {
    if (character === '"' && previousCharacter !== '\\') inQuotes = !inQuotes
    if (character !== ' ' || inQuotes || previousCharacter !== ' ') newQuery += character
    previousCharacter = character
  }

  return newQuery.trim()
}

export const replaceTokensDifferentially = (searchQuery: string, newTokens: string[], type: TokenType) => {
  const existingTokens = getTokensByType(searchQuery, type)
  const queryWithoutNoFilter = searchQuery.replace(`no:${type}`, '')
  const tokensToAdd = newTokens.filter(token => !existingTokens.includes(token))
  const tokensToRemove = existingTokens.filter(token => !newTokens.includes(token))

  let newQuery = queryWithoutNoFilter
  for (const token of tokensToRemove) {
    newQuery = newQuery.replace(new RegExp(`-?${type}:${escapeRegExp(token)}`, 'g'), '')
  }
  for (const token of tokensToAdd) {
    newQuery = token === `no:${type}` ? `${newQuery} ${token} ` : `${newQuery} ${type}:${escapeString(token)}`
  }

  return cleanConsecutiveSpaces(newQuery)
}

export const replaceAllFiltersByTypeInSearchQuery = (
  searchQuery: string,
  tokens: string[],
  type: TokenType,
  isNegatedFilter?: boolean,
) => {
  const cleanSearchQuery = searchQuery.replace(tokensByType(type), '').replace(`no:${type}`, '').trim()
  if (!tokens.length) return cleanConsecutiveSpaces(cleanSearchQuery)

  const formattedTokens = tokens.map(token => (token.includes(' ') ? `"${token}"` : token))
  const newQuery = isNegatedFilter ? `no:${type}` : `${type}:${formattedTokens.join(',')}`

  return cleanConsecutiveSpaces(`${cleanSearchQuery} ${newQuery}`)
}
